import { useEffect, useRef, useState } from 'react';

interface Bar {
  label: string;
  value: number;
  /** Optional secondary value drawn as a darker inset (e.g. premium subset). */
  inset?: number;
}

interface Props {
  data: Bar[];
  height?: number;
}

/**
 * Lightweight vertical bar chart for COUNTS (signups, etc.) — sibling to
 * EarningsChart, which is money-only. Inline SVG, no dependency. The inset
 * bar (if provided) renders the premium subset in the brand color over a
 * muted base bar.
 */
export function CountBars({ data, height = 200 }: Props) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [w, setW] = useState(800);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setW(Math.max(320, e.contentRect.width));
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  const padT = 12;
  const padB = 26;
  const padL = 8;
  const padR = 8;
  const innerW = w - padL - padR;
  const innerH = height - padT - padB;
  const max = Math.max(1, ...data.map((d) => d.value));
  const slot = innerW / Math.max(1, data.length);
  const barW = Math.min(40, slot * 0.6);

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      <svg width={w} height={height} style={{ display: 'block' }}>
        {data.map((d, i) => {
          const x = padL + slot * i + (slot - barW) / 2;
          const h = (innerH * d.value) / max;
          const y = padT + innerH - h;
          const insetH = d.inset != null ? (innerH * d.inset) / max : 0;
          const insetY = padT + innerH - insetH;
          const isHover = hover === i;
          return (
            <g
              key={i}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            >
              <rect
                x={x}
                y={y}
                width={barW}
                height={Math.max(0, h)}
                rx={4}
                fill="var(--brand-soft)"
                opacity={isHover ? 1 : 0.85}
              />
              {d.inset != null && (
                <rect x={x} y={insetY} width={barW} height={Math.max(0, insetH)} rx={4} fill="var(--brand)" />
              )}
              {(i % 2 === 0 || i === data.length - 1) && (
                <text x={x + barW / 2} y={height - 8} fontSize="10" textAnchor="middle" fill="var(--ink-3)" fontFamily="var(--font-mono)">
                  {d.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {hover != null && data[hover] && (
        <div
          style={{
            position: 'absolute',
            left: Math.min(w - 150, Math.max(0, padL + slot * hover)),
            top: 0,
            background: 'var(--ink)',
            color: 'var(--bg-elev)',
            padding: '8px 10px',
            borderRadius: 8,
            fontSize: 12,
            pointerEvents: 'none',
            boxShadow: 'var(--shadow-elev)',
            whiteSpace: 'nowrap',
          }}
        >
          <div style={{ opacity: 0.6, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {data[hover].label}
          </div>
          <div className="num" style={{ fontWeight: 600, fontSize: 14 }}>
            {data[hover].value.toLocaleString('en-US')}
            {data[hover].inset != null && (
              <span style={{ opacity: 0.6, fontWeight: 400 }}> · {data[hover].inset} premium</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
